import { pathSegments } from '../readOwnPath';
import { warnOnce } from '../warnOnce';
import { DECISIONS_CONTEXT_KEY } from './embeddedDecisions';
import {
  checkFetchPrecondition,
  type FetchPrecondition,
} from '../data-sources/fetchWhen';
import {
  NO_FACT_BINDINGS,
  type RefusedBinding,
  type FactBinding,
  type FactBindings,
} from './types';

/** The DSL node type that declares decision fact bindings. */
export const VARIANT_TYPE = 'platform.variant';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim() !== '';

/**
 * The fields a `bindings` entry carries. An entry missing a required field,
 * or holding a blank one, is dropped; other fields are ignored. The shared
 * parity fixture `__tests__/fixtures/fact-bindings.json` pins these lists.
 */
export const FACT_BINDING_REQUIRED_FIELDS = [
  'factKey',
  'source',
  'path',
] as const;
export const FACT_BINDING_OPTIONAL_FIELDS = ['when'] as const;

/**
 * Reads a binding may not make; such a binding is refused and its fact is
 * absent. No decision reads another decision's outcome, so the rule results
 * the host embedded under `context.decisions` are never a decision input.
 */
export const FACT_BINDING_REFUSED_READS: ReadonlyArray<{
  readonly source: string;
  readonly pathRoot: string;
}> = [{ source: 'context', pathRoot: DECISIONS_CONTEXT_KEY }];

type ReadBinding =
  | { readonly binding: FactBinding }
  | { readonly refused: RefusedBinding }
  | null;

function readBinding(value: unknown): ReadBinding {
  if (!isRecord(value)) return null;
  if (!FACT_BINDING_REQUIRED_FIELDS.every((f) => isNonEmptyString(value[f]))) {
    return null;
  }
  const { factKey, source, path, when } = value as Record<
    (typeof FACT_BINDING_REQUIRED_FIELDS)[number],
    string
  > & { when?: unknown };
  const binding = {
    factKey: factKey.trim(),
    source: source.trim(),
    path: path.trim(),
  };
  if (
    FACT_BINDING_REFUSED_READS.some(
      (read) =>
        binding.source === read.source &&
        pathSegments(binding.path)[0] === read.pathRoot,
    )
  ) {
    return {
      refused: {
        factKey: binding.factKey,
        reason: `A fact may not read a rule result ("context:${binding.path}").`,
      },
    };
  }
  if (when === undefined) return { binding };
  const check = checkFetchPrecondition(when);
  if ('reason' in check) {
    return { refused: { factKey: binding.factKey, reason: check.reason } };
  }
  return { binding: { ...binding, when: [check.precondition] } };
}

const sameBinding = (a: FactBinding, b: FactBinding) =>
  a.source === b.source && a.path === b.path;

function addPreconditions(
  into: FetchPrecondition[],
  next: readonly FetchPrecondition[],
): FetchPrecondition[] {
  for (const precondition of next) {
    const key = JSON.stringify(precondition);
    if (!into.some((item) => JSON.stringify(item) === key)) {
      into.push(precondition);
    }
  }
  return into;
}

/**
 * Two declarations of one fact read the same field. When either is
 * unconditional the fact always is; otherwise it is bound while any of their
 * preconditions holds.
 */
function mergeWhen(existing: FactBinding, next: FactBinding): FactBinding {
  if (!existing.when) return existing;
  if (!next.when) {
    return {
      factKey: existing.factKey,
      source: existing.source,
      path: existing.path,
    };
  }
  return {
    ...existing,
    when: addPreconditions([...existing.when], next.when),
  };
}

/**
 * Walks a whole DSL document, including every slot of every variant and the
 * legacy `zones` map, and collects the fact bindings each `platform.variant`
 * declares. The binding is declared in the DSL config wherever the fetch
 * runs: the browser's DataSourceManager uses it to decide whether a source
 * is fetched, and a server-side runner can use the same bindings to build
 * the facts it posts.
 *
 * A fact is a reference to one field, so two variants that bind the same
 * fact must bind it the same way; when they disagree the first declaration
 * wins and the conflict is reported once. When they agree but carry
 * different preconditions, the fact is bound while either holds.
 *
 * A binding whose precondition is not allowed, for example one that reads a
 * decision outcome, is refused: its fact stays absent. So is a binding that
 * reads `context.decisions`, because no decision reads another's outcome.
 */
export function collectFactBindings(data: unknown): FactBindings {
  const bindingsByFact = new Map<string, FactBinding>();
  const refused: RefusedBinding[] = [];

  const visit = (value: unknown): void => {
    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }
    if (!isRecord(value)) return;

    if (value.type === VARIANT_TYPE && isRecord(value.props)) {
      const { bindings } = value.props;
      for (const raw of Array.isArray(bindings) ? bindings : []) {
        const read = readBinding(raw);
        if (!read) continue;
        if ('refused' in read) {
          refused.push(read.refused);
          warnOnce(
            'platform.variant',
            `[platform.variant] Fact "${read.refused.factKey}" is not bound: ${read.refused.reason}`,
          );
          continue;
        }
        const { binding } = read;
        const existing = bindingsByFact.get(binding.factKey);
        if (!existing) {
          bindingsByFact.set(binding.factKey, binding);
        } else if (sameBinding(existing, binding)) {
          bindingsByFact.set(binding.factKey, mergeWhen(existing, binding));
        } else {
          warnOnce(
            'platform.variant',
            `[platform.variant] Fact "${binding.factKey}" is bound to ${existing.source}:${existing.path} and ${binding.source}:${binding.path}; the first binding is used.`,
          );
        }
      }
    }

    for (const child of Object.values(value)) visit(child);
  };

  visit(isRecord(data) ? [data.content, data.zones] : data);
  if (bindingsByFact.size === 0 && refused.length === 0) {
    return NO_FACT_BINDINGS;
  }
  return { bindings: Array.from(bindingsByFact.values()), refused };
}

/**
 * Data sources that only conditional bindings read, each with the
 * preconditions under which one of those bindings needs it. A source any
 * unconditional binding reads is not listed: it is fetched as before.
 */
export function conditionalSources(
  factBindings: FactBindings,
  reservedRoots: ReadonlySet<string>,
): ReadonlyMap<string, readonly FetchPrecondition[]> {
  const gates = new Map<string, FetchPrecondition[] | null>();
  for (const binding of factBindings.bindings) {
    if (reservedRoots.has(binding.source)) continue;
    const current = gates.get(binding.source);
    if (current === null) continue;
    if (!binding.when) {
      gates.set(binding.source, null);
      continue;
    }
    gates.set(binding.source, addPreconditions(current ?? [], binding.when));
  }
  const result = new Map<string, readonly FetchPrecondition[]>();
  gates.forEach((preconditions, source) => {
    if (preconditions) result.set(source, preconditions);
  });
  return result;
}
